export interface LedgerRow {
  label: string;
  value: string;
  /** Sub-item under the row above (e.g. "Contractor estimates" under "Estimated"). */
  indent?: boolean;
  /** The page's single most-important figure (e.g. Remaining balance due) — ember/orange accent. */
  accent?: boolean;
  /** The other headline figure (e.g. Total project cost) — bold serif, no color accent. */
  headline?: boolean;
  /** Rule above this row, separating a headline group from its supporting detail. */
  dividerAbove?: boolean;
}

interface LedgerTableProps {
  rows: LedgerRow[];
  /** 'sm' for compact contexts (a task card); defaults to the full project-header size. */
  size?: 'sm' | 'md';
}

/** Bordered two-column (label | amount) table for a budget summary — a spreadsheet-style
 * alternative to prose sentences, used identically on the project header, task cards, and the
 * client/project list rollups so the "money" parts of the app read as one system. */
export function LedgerTable({ rows, size = 'md' }: LedgerTableProps) {
  const cellPad = size === 'sm' ? 'px-2 py-1' : 'px-3 py-1.5';
  const labelSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const headlineValueSize = size === 'sm' ? 'text-sm' : 'text-base';

  return (
    <table className="w-full border-collapse overflow-hidden rounded-md border border-gray-200 bg-white">
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={i}
            className={
              row.dividerAbove
                ? 'border-t-2 border-gray-300'
                : i > 0
                  ? 'border-t border-gray-100'
                  : ''
            }
          >
            <td
              className={`w-full whitespace-nowrap ${cellPad} ${labelSize} ${row.indent ? 'pl-6 text-gray-500' : ''} ${
                row.headline ? 'font-serif font-semibold text-phoenix-blue-900' : ''
              } ${row.accent ? 'font-medium text-orange-700' : !row.indent && !row.headline ? 'text-gray-600' : ''}`}
            >
              {row.label}
            </td>
            <td
              className={`whitespace-nowrap ${cellPad} text-right font-medium tabular-nums ${labelSize} ${
                row.headline
                  ? `font-serif ${headlineValueSize} font-bold text-phoenix-blue-900`
                  : row.accent
                    ? 'font-semibold text-orange-700'
                    : 'text-gray-900'
              }`}
            >
              {row.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
